import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, Package, Loader2 } from "lucide-react";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => {}).finally(() => setLoading(false));
  }, [orderId, user]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-5">
        <CheckCircle2 className="w-9 h-9 text-teal" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-navy">Payment successful!</h1>
      <p className="text-slate-500 mt-2">Thank you for your purchase. A confirmation has been sent to your email.</p>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-teal mx-auto mt-8" /> : order ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 mt-8 text-left">
          <div className="flex justify-between text-sm mb-4">
            <span className="text-slate-500">Order Number</span>
            <span className="font-mono font-semibold text-navy" data-testid="success-order-no">{order.order_no}</span>
          </div>
          <div className="space-y-2 border-t border-slate-100 pt-4">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-slate-700">{i.name} × {i.quantity}</span>
                <span className="font-medium">{inr(i.line_total)}</span>
              </div>
            ))}
            <div className="flex justify-between text-base font-bold text-navy border-t border-slate-100 pt-3 mt-1">
              <span>Total Paid</span><span>{inr(order.total)}</span>
            </div>
          </div>
          {order.digital_delivered && (
            <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800 flex items-center gap-2">
              <Download className="w-4 h-4" /> Your digital products are ready in your library.
            </div>
          )}
          {order.requires_shipping && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
              <Package className="w-4 h-4" /> Your physical items are being processed for shipping.
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400 mt-8">Sign in to view your order details and library.</p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
        {user ? (
          <>
            <Button asChild className="bg-teal hover:bg-teal-dark text-white"><Link to="/account/library">Open My Library</Link></Button>
            <Button asChild variant="outline"><Link to="/account/orders">View Orders</Link></Button>
          </>
        ) : (
          <Button asChild className="bg-teal hover:bg-teal-dark text-white"><Link to="/register">Create account to access library</Link></Button>
        )}
        <Button asChild variant="ghost"><Link to="/shop">Continue shopping</Link></Button>
      </div>
    </div>
  );
}
